import { QuestionnaireCard } from "../components/questionnaire";
import { Button } from "../components/ui/button";
import { Icon } from "../components/ui/icons";
import { BackButton, Centered, IconBadge, Screen } from "../components/ui/layout";
import { cn } from "../libs/cn";
import type { PickResult, PickScope } from "../modules/pick/api";
import { questionnaireComplete, type Questionnaire, type QuestionnaireAnswers } from "../modules/states/tree";

import type { Common } from "./screens";

// Подбор изделий по анкете «Что искал»: та же анкета, что при отказе, только здесь по ней 1С ищет изделия.
// Живого счётчика нет — гонять схему подбора на каждый тап нельзя; цифры приходят с результатом.

const SCOPES: { id: PickScope; name: string }[] = [
  { id: "shop", name: "В этом салоне" },
  { id: "network", name: "Вся сеть" },
];

export function PickScreen({
  busy,
  error,
  questionnaire,
  answers,
  scope,
  onChange,
  onScope,
  onBack,
  onPick,
}: Common & {
  questionnaire: Questionnaire;
  answers: QuestionnaireAnswers;
  scope: PickScope;
  onChange: (answers: QuestionnaireAnswers) => void;
  onScope: (scope: PickScope) => void;
  onBack: () => void;
  onPick: () => void;
}) {
  const ready = questionnaireComplete(questionnaire, answers);
  return (
    <Screen
      error={error}
      header={<BackButton onClick={onBack} />}
      footer={
        <>
          <Button size="lg" loading={busy} disabled={!ready} onClick={onPick}>
            <Icon name="sparkle" className="size-5" />
            Подобрать
          </Button>
          {!ready && (
            <p className="text-center text-xs text-[var(--color-muted)]">Заполните отмеченные * поля анкеты</p>
          )}
        </>
      }
    >
      <h1 className="text-2xl font-semibold text-balance">Что ищет клиент?</h1>
      <p className="pt-1 pb-3 text-sm text-[var(--color-muted)]">Отметьте, что уже понятно. Пусто — значит «любой».</p>
      <QuestionnaireCard
        questionnaire={questionnaire}
        answers={answers}
        index={null}
        title={false}
        onChange={onChange}
      />
      <div className="mt-3 flex rounded-2xl bg-black/5 p-1">
        {SCOPES.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={item.id === scope}
            onClick={() => onScope(item.id)}
            className={cn(
              "min-h-11 flex-1 rounded-xl px-2 text-sm font-semibold transition-[background-color,color]",
              item.id === scope
                ? "bg-[var(--color-surface)] text-[var(--color-ink)] shadow-sm"
                : "text-[var(--color-muted)]",
            )}
          >
            {item.name}
          </button>
        ))}
      </div>
      <p className="pt-2 text-center text-xs text-[var(--color-muted)]">
        Список посчитает 1С по остаткам и ценам. Если ничего не найдётся — ответы станут заявкой в товарную матрицу.
      </p>
    </Screen>
  );
}

// Результат — HTML от 1С в изолированном фрейме (как карточка сканера) и цифры по салону и сети.
export function PickResultScreen({
  busy,
  error,
  summary,
  scope,
  result,
  canDemand,
  onBack,
  onNetwork,
  onDemand,
}: Common & {
  // Запрос одной строкой: «Кольцо · Золото 585 · до 70 000 ₽».
  summary: string;
  scope: PickScope;
  result: PickResult;
  // В консультации «ничего не подошло» записывается запросом клиента; с главной — нет.
  canDemand: boolean;
  onBack: () => void;
  onNetwork: () => void;
  onDemand: () => void;
}) {
  const { total, shown, inShop, inNetwork, truncated } = result.summary;
  const empty = total === 0;
  // Наличие — словами: продавец в другом салоне не должен гадать, что значит «привезти» (замечание ЗЛ, 29.10).
  const counts =
    scope === "shop"
      ? `В этом салоне ${inShop}${inNetwork !== undefined ? ` · по сети ${inNetwork}` : ""}`
      : `По сети ${total} · в этом салоне ${inShop} · в других салонах ${total - inShop}`;
  const canNetwork = scope === "shop" && (empty ? Boolean(inNetwork) : true);

  return (
    <Screen
      error={error}
      header={<BackButton onClick={onBack}>Изменить</BackButton>}
      footer={
        !empty && (
          <>
            {canNetwork && (
              <Button variant="secondary" size="md" loading={busy} onClick={onNetwork}>
                Показать по всей сети
              </Button>
            )}
            {canDemand && (
              <Button variant="ghost" size="sm" className="self-center" onClick={onDemand}>
                Ничего не подошло → записать запрос
              </Button>
            )}
          </>
        )
      }
    >
      <div className="mb-2 rounded-2xl bg-[var(--color-surface)] px-4 py-3 ring-1 ring-[var(--color-line)]">
        <p className="font-semibold text-balance">{summary}</p>
        <p className="text-sm text-[var(--color-muted)]">{counts}</p>
        {truncated && (
          <p className="text-xs text-[var(--color-gold)]">
            Показаны {shown} из {total} — уточните запрос
          </p>
        )}
      </div>

      {empty ? (
        <Centered>
          <IconBadge tone="muted" name="sparkle" size="lg" />
          <h2 className="text-xl font-semibold">{scope === "shop" ? "В этом салоне ничего нет" : "По сети ничего нет"}</h2>
          <div className="flex w-full flex-col gap-2 pt-2">
            {canNetwork && (
              <Button variant="secondary" size="md" loading={busy} onClick={onNetwork}>
                Показать по всей сети · {inNetwork}
              </Button>
            )}
            <Button variant="secondary" size="md" onClick={onBack}>
              Изменить запрос
            </Button>
            {canDemand && (
              <Button size="md" onClick={onDemand}>
                <Icon name="receipt" className="size-5" />
                Записать как запрос клиента
              </Button>
            )}
          </div>
          {canDemand && (
            <p className="max-w-72 text-center text-xs text-[var(--color-muted)]">
              Запрос попадёт в «Уже отмечено» на итогах и в заявку на товарную матрицу.
            </p>
          )}
        </Centered>
      ) : (
        // sandbox без allow-scripts: от 1С приходит только разметка и стили. Свой скролл внутри фрейма.
        // Минимум высоты: при крупном системном шрифте шапка и кнопки съедали экран, и от списка оставалась
        // одна строка (Samsung, масштаб шрифта — тестер ЗЛ 29.10). Теперь main прокручивается, а фрейм не сжимается.
        <iframe
          title="Подобранные изделия"
          srcDoc={result.html}
          sandbox=""
          className="min-h-[55dvh] w-full flex-1 rounded-2xl bg-white ring-1 ring-[var(--color-line)]"
        />
      )}
    </Screen>
  );
}
